import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Collapse,
  IconButton,
  Skeleton,
  Alert,
  Button,
  Chip,
  alpha,
  useTheme,
  Fade,
  LinearProgress,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import RefreshIcon from '@mui/icons-material/Refresh';
import StopIcon from '@mui/icons-material/Stop';
import MarkdownContent from '../MarkdownContent';
import { iaService, ChatRequest } from '../../services/api';
import { useNavigate } from 'react-router-dom';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const CACHE_KEY_PREFIX = 'analise-ia';

const SAUDE_PROMPT = `Gere uma analise estrategica completa dos indicadores APS deste municipio.
Produza EXATAMENTE as 5 secoes abaixo, usando H3. Seja conciso e acionavel. Use dados reais do contexto.

### Diagnostico Municipal
Avaliacao geral em 3-4 frases: score, classificacao, pontos fortes (>75), fracos (<50), e comparacao com quadrimestre anterior (se dados de evolucao disponiveis). Mencione eixos com links internos.

### Impacto Financeiro
Calcule quanto o municipio deixa de receber por nao estar Otimo. Gap mensal e anual estimado. Top 2-3 equipes com maior potencial de ganho financeiro.

### Acoes Prioritarias para Este Mes
3-5 acoes concretas com: indicador alvo (com link para eixo), o que fazer (ex: busca ativa), quantas pessoas faltam atender (use numerador/denominador), impacto estimado no score.

### Projecao de Risco
Baseado nas tendencias de evolucao (se disponiveis), o que acontece no proximo quadrimestre sem acao. Eixos com tendencia de queda. Se nao ha dados de evolucao, indique riscos baseados nos eixos mais criticos.

### Vitoria Rapida
O UNICO indicador com melhor relacao esforco/impacto: poucas acoes para subir significativamente o score. O que fazer, para quantas pessoas, ganho esperado. Use um chart com as top 5 oportunidades por potencial de pontos.`;

const MONITORING_PROMPT = `Analise os dados deste painel de monitoramento e produza EXATAMENTE as 4 secoes abaixo usando H3. Seja conciso, objetivo e acionavel. Use exclusivamente dados reais do contexto fornecido.

### Situacao Atual
Avaliacao geral em 3-4 frases: principais indicadores, valores criticos, destaques positivos e negativos, e comparacao com periodos anteriores se disponivel.

### Alertas e Pontos Criticos
Valores fora da normalidade, tendencias preocupantes, gargalos operacionais. Destaque em **negrito** os itens que requerem acao imediata. Se nenhum alerta critico, indique os pontos que merecem atencao.

### Acoes Recomendadas
3-5 acoes concretas e prioritarias baseadas nos dados. Para cada acao: o que fazer, qual indicador impacta, resultado esperado.

### Projecao e Riscos
Tendencias esperadas para os proximos meses baseadas nos dados. O que acontece sem intervencao. Oportunidades de melhoria rapida.`;

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface AnalisePredicaoCardProps {
  dadosContexto?: string;
  municipioId: number;
  quadrimestre?: string;
  ano?: number;
  /** Identificador do painel para cache (ex: 'leitos', 'vacinas', 'financeiro') */
  painelId?: string;
  /** Prompt customizado para a analise. Se omitido, usa prompt padrao baseado no contexto */
  prompt?: string;
  /** Contexto enviado ao backend: 'saude' ou 'dashboard' */
  contexto?: 'saude' | 'dashboard';
  /** Titulo do card (default: 'Analise Estrategica IA') */
  titulo?: string;
  /** Subtitulo exibido quando nao ha resumo */
  subtitulo?: string;
  /** Se false, dispara automaticamente ao carregar. Default: true (so analisa ao abrir o painel) */
  manualTrigger?: boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const AnalisePredicaoCard: React.FC<AnalisePredicaoCardProps> = ({
  dadosContexto,
  municipioId,
  quadrimestre,
  ano,
  painelId,
  prompt,
  contexto = 'saude',
  titulo = 'Analise Estrategica IA',
  subtitulo,
  manualTrigger = true,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [expanded, setExpanded] = useState(false);
  const [analysisText, setAnalysisText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(false);

  const abortRef = useRef<AbortController | null>(null);
  const hasTriggered = useRef(false);

  const effectivePrompt = prompt || (contexto === 'dashboard' ? MONITORING_PROMPT : SAUDE_PROMPT);

  const cacheKey = useMemo(() => {
    const parts = [CACHE_KEY_PREFIX];
    if (painelId) parts.push(painelId);
    parts.push(String(municipioId));
    if (quadrimestre) parts.push(quadrimestre);
    if (ano) parts.push(String(ano));
    return parts.join('-');
  }, [painelId, municipioId, quadrimestre, ano]);

  // Load from cache on mount / context change
  useEffect(() => {
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      setAnalysisText(cached);
      setHasGenerated(true);
      if (!manualTrigger) setExpanded(true);
    } else {
      setAnalysisText('');
      setHasGenerated(false);
      hasTriggered.current = false;
    }
  }, [cacheKey, manualTrigger]);

  // Generate analysis via streaming
  const generateAnalysis = useCallback(async () => {
    if (!dadosContexto || loading) return;

    setLoading(true);
    setIsStreaming(true);
    setError(null);
    setAnalysisText('');
    if (!manualTrigger) setExpanded(true);

    const request: ChatRequest = {
      municipio_id: municipioId,
      contexto,
      ...(quadrimestre && ano ? { quadrimestre: `Q${quadrimestre}-${ano}` } : {}),
      pergunta: effectivePrompt,
      dados_contexto: dadosContexto,
    };

    try {
      const { reader: readerPromise, abort } = iaService.chatStream(request);
      abortRef.current = abort;
      const reader = await readerPromise;
      if (!reader) throw new Error('Streaming nao suportado');

      const decoder = new TextDecoder();
      let buffer = '';
      let fullText = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');
        buffer = lines.pop() || '';

        for (const block of lines) {
          const trimmed = block.trim();
          if (!trimmed || trimmed === 'data: [DONE]') continue;
          const match = trimmed.match(/^data:\s*(.+)$/s);
          if (!match) continue;
          try {
            const event = JSON.parse(match[1]);
            if (event.text) {
              fullText += event.text;
              setAnalysisText(fullText);
            } else if (event.error) {
              throw new Error(event.error);
            }
          } catch (e: any) {
            if (e.message && !e.message.includes('JSON')) throw e;
          }
        }
      }

      if (fullText) {
        sessionStorage.setItem(cacheKey, fullText);
        setHasGenerated(true);
      }
    } catch (err: any) {
      if (err.name === 'AbortError') {
        // Keep whatever was generated
        if (analysisText) {
          sessionStorage.setItem(cacheKey, analysisText);
          setHasGenerated(true);
        }
      } else {
        // Fallback to non-streaming
        try {
          const result = await iaService.chat(request);
          setAnalysisText(result.resposta);
          sessionStorage.setItem(cacheKey, result.resposta);
          setHasGenerated(true);
        } catch {
          setError('Nao foi possivel gerar a analise. Verifique a conexao com o provedor de IA.');
        }
      }
    } finally {
      setLoading(false);
      setIsStreaming(false);
      abortRef.current = null;
    }
  }, [dadosContexto, municipioId, contexto, quadrimestre, ano, effectivePrompt, cacheKey, loading, analysisText, manualTrigger]);

  // Auto-trigger on first data load (only if not cached and not manual)
  useEffect(() => {
    if (!manualTrigger && dadosContexto && !hasGenerated && !hasTriggered.current && !loading) {
      hasTriggered.current = true;
      generateAnalysis();
    }
  }, [manualTrigger, dadosContexto, hasGenerated, loading, generateAnalysis]);

  // Cleanup on unmount
  useEffect(() => {
    return () => { abortRef.current?.abort(); };
  }, []);

  const handleRefresh = () => {
    sessionStorage.removeItem(cacheKey);
    setHasGenerated(false);
    hasTriggered.current = false;
    generateAnalysis();
  };

  const handleStop = () => {
    abortRef.current?.abort();
  };

  const handleNavigate = useCallback((path: string) => {
    navigate(path);
  }, [navigate]);

  // Header click: toggle expand; gera analise na primeira abertura
  const handleHeaderClick = useCallback(() => {
    const willExpand = !expanded;
    setExpanded(willExpand);
    if (willExpand && !hasGenerated && !loading && dadosContexto) {
      generateAnalysis();
    }
  }, [expanded, hasGenerated, loading, dadosContexto, generateAnalysis]);

  // Extract first paragraph as summary for collapsed view
  const summaryText = useMemo(() => {
    if (!analysisText) return '';
    const lines = analysisText.split('\n');
    for (const line of lines) {
      const trimmed = line.trim();
      if (trimmed && !trimmed.startsWith('#') && !trimmed.startsWith('---') && trimmed.length > 20) {
        return trimmed.length > 150 ? trimmed.slice(0, 150) + '...' : trimmed;
      }
    }
    return '';
  }, [analysisText]);

  const defaultSubtitulo = contexto === 'dashboard'
    ? 'Situacao atual, alertas criticos e acoes recomendadas'
    : 'Diagnostico, impacto financeiro e acoes prioritarias';

  if (!dadosContexto) return null;

  return (
    <Fade in timeout={860}>
      <Card
        elevation={0}
        sx={{
          borderRadius: 3,
          border: `1px solid ${theme.palette.divider}`,
          mb: 3,
          overflow: 'hidden',
          background: `linear-gradient(135deg, ${alpha('#7c4dff', 0.03)} 0%, ${alpha('#448aff', 0.03)} 100%)`,
        }}
      >
        {/* Clickable header */}
        <CardContent
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            cursor: 'pointer',
            py: 2,
            '&:last-child': { pb: 2 },
          }}
          onClick={handleHeaderClick}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              bgcolor: alpha('#7c4dff', 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#7c4dff',
              flexShrink: 0,
            }}
          >
            <AutoAwesomeIcon />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              {titulo}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {loading
                ? 'Gerando analise...'
                : summaryText
                  || subtitulo
                  || (!hasGenerated ? 'Clique para expandir e gerar a analise' : defaultSubtitulo)}
            </Typography>
          </Box>
          {loading && (
            <Chip
              label="Gerando..."
              size="small"
              sx={{
                bgcolor: alpha('#7c4dff', 0.1),
                color: '#7c4dff',
                fontWeight: 600,
                fontSize: '0.7rem',
              }}
            />
          )}
          {hasGenerated && !loading && (
            <Chip
              label="Pronta"
              size="small"
              sx={{
                bgcolor: alpha('#4caf50', 0.1),
                color: '#4caf50',
                fontWeight: 600,
                fontSize: '0.7rem',
              }}
            />
          )}
          <IconButton
            size="small"
            sx={{
              transform: expanded ? 'rotate(180deg)' : 'none',
              transition: 'transform 0.2s',
            }}
          >
            <ExpandMoreIcon />
          </IconButton>
        </CardContent>

        {/* Loading bar */}
        {loading && (
          <LinearProgress
            sx={{
              height: 2,
              '& .MuiLinearProgress-bar': { bgcolor: '#7c4dff' },
              bgcolor: alpha('#7c4dff', 0.1),
            }}
          />
        )}

        <Collapse in={expanded}>
          <Box
            sx={{
              px: 2.5,
              pb: 2,
              borderTop: `1px solid ${theme.palette.divider}`,
            }}
          >
            {/* Action buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, pt: 1.5, mb: 1 }}>
              {isStreaming ? (
                <Button
                  size="small"
                  color="error"
                  startIcon={<StopIcon />}
                  onClick={handleStop}
                  sx={{ textTransform: 'none' }}
                >
                  Parar
                </Button>
              ) : (
                <Button
                  size="small"
                  startIcon={<RefreshIcon />}
                  onClick={handleRefresh}
                  disabled={loading || !dadosContexto}
                  sx={{ textTransform: 'none' }}
                >
                  Atualizar analise
                </Button>
              )}
            </Box>

            {/* Error */}
            {error && (
              <Alert severity="error" sx={{ borderRadius: 2, mb: 2 }}>
                {error}
                <Button size="small" onClick={handleRefresh} sx={{ ml: 1 }}>
                  Tentar novamente
                </Button>
              </Alert>
            )}

            {/* Loading skeleton */}
            {loading && !analysisText && (
              <Box sx={{ py: 1 }}>
                <Skeleton variant="text" width="60%" height={28} />
                <Skeleton variant="text" width="90%" />
                <Skeleton variant="text" width="85%" />
                <Skeleton variant="text" width="70%" />
                <Skeleton variant="rectangular" height={80} sx={{ borderRadius: 1, mt: 1, mb: 2 }} />
                <Skeleton variant="text" width="50%" height={28} />
                <Skeleton variant="text" width="88%" />
                <Skeleton variant="text" width="75%" />
              </Box>
            )}

            {/* Analysis content */}
            {analysisText && (
              <MarkdownContent content={analysisText} onNavigate={handleNavigate} />
            )}
          </Box>
        </Collapse>
      </Card>
    </Fade>
  );
};

export default AnalisePredicaoCard;
